import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Form, Alert, Row, Col } from 'react-bootstrap';
import { HomeOutlined } from '@ant-design/icons';
import { LimsNavIcon } from '../components/LimsNavIcon';
import { NotificationPreferencesPanel } from '../components/NotificationPreferencesPanel';
import './MemberManagement.css';
import '../styles/resourceManagement.css';
import { useAuth } from '../contexts/AuthContext';
import { User } from '../types';
import { getCurrentUser, changePassword, getMembers, updateProfile, getNotificationPreferences, updateNotificationPreferences, NotificationPreferences } from '../services/api';
import { getDefaultAvatarByGender, defaultAvatar } from '../assets/avatars';
import { literatureService, LiteratureStorageSummary } from '../services/literature/literatureService';
import { resolveUploadAssetUrl } from '../utils/resolveUploadAssetUrl';

function maskWechatOpenid(id?: string | null): string {
  if (!id || typeof id !== 'string') return '';
  const s = id.trim();
  if (!s) return '';
  if (s.length <= 10) return '***';
  return `${s.slice(0, 4)}…${s.slice(-4)}`;
}

function formatProfileDateTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('zh-CN');
}

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { token, username, role, realName, avatar, updateUserInfo } = useAuth();
  const [userInfo, setUserInfo] = useState<Partial<User>>({
    name: realName || username || '用户',
    gender: '男',
    studentId: '20230001',
    phone: '13800000000',
    email: 'user@example.com',
    supervisor: '李老师',
    enrollmentYear: new Date().getFullYear(),
    studentType: '研究生',
    role: (role as '超级管理员' | '管理员' | '普通用户') || '普通用户',
    avatar: avatar || defaultAvatar,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [teachers, setTeachers] = useState<User[]>([]);
  
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences | null>(null);
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [literatureStorage, setLiteratureStorage] = useState<LiteratureStorageSummary | null>(null);

  useEffect(() => {
    // 获取当前用户信息
    const fetchUserInfo = async () => {
      try {
        setLoading(true);
        const response = await getCurrentUser();
        if (response.success && response.data) {
          setUserInfo(response.data);
        } else {
          // 如果API失败，使用上下文中的基本信息
          setUserInfo(prev => ({ 
            ...prev, 
            name: realName || username || undefined,
            username: username || undefined,
            role: (role as '超级管理员' | '管理员' | '普通用户') || '普通用户',
            avatar: avatar || defaultAvatar
          }));
        }
      } catch (error) {
        console.error('获取用户信息失败:', error);
        // 使用上下文中的基本信息作为后备
        setUserInfo(prev => ({ 
          ...prev, 
          name: realName || username || undefined,
          username: username || undefined,
          role: (role as '超级管理员' | '管理员' | '普通用户') || '普通用户',
          avatar: avatar || defaultAvatar
        }));
      } finally {
        setLoading(false);
      }
    };

    // 获取教师列表用于导师选择
    const fetchTeachers = async () => {
      try {
        const response = await getMembers();
        if (response.success) {
          // 筛选出教师作为导师选项
          setTeachers(response.data?.filter((member: User) => member.studentType === '教师') || []);
        }
      } catch (error) {
        console.error('获取教师列表失败:', error);
      }
    };

    const fetchLitStorage = async () => {
      try {
        const s = await literatureService.getStorageSummary();
        setLiteratureStorage(s);
      } catch {
        setLiteratureStorage(null);
      }
    };

    if (token) {
      fetchUserInfo();
      fetchTeachers();
      fetchLitStorage();
    }
  }, [token, username, realName, role, avatar]);

  // 获取通知偏好设置
  const fetchNotificationPreferences = useCallback(async () => {
    try {
      setLoadingPreferences(true);
      const response = await getNotificationPreferences();
      if (response.success && response.data) {
        let preferences = response.data as NotificationPreferences;
        
        // 如果用户没有邮箱，自动关闭所有邮件通知
        if (!userInfo.email) {
          preferences = {
            ...preferences,
            instrument: { ...preferences.instrument, emailEnabled: false },
            literature: { ...preferences.literature, emailEnabled: false },
            consumable: { ...preferences.consumable, emailEnabled: false },
            approval: { ...preferences.approval, emailEnabled: false },
            feedback: preferences.feedback
              ? {
                  ...preferences.feedback,
                  emailEnabled: false,
                  scenarios: {
                    ...preferences.feedback.scenarios,
                    weeklySummary:
                      preferences.feedback.scenarios.weeklySummary ?? true
                  }
                }
              : {
                  enabled: true,
                  emailEnabled: false,
                  scenarios: {
                    newPost: true,
                    officialReply: true,
                    statusAccepted: true,
                    statusInProgress: true,
                    statusResolved: true,
                    statusRejected: true,
                    reportProcessed: true,
                    weeklySummary: true
                  }
                }
          };
        } else {
          // 补齐可能缺失的 feedback 配置（兼容老数据）
          if (!preferences.feedback) {
            preferences = {
              ...preferences,
              feedback: {
                enabled: true,
                emailEnabled: true,
                scenarios: {
                  newPost: true,
                  officialReply: true,
                  statusAccepted: true,
                  statusInProgress: true,
                  statusResolved: true,
                  statusRejected: true,
                  reportProcessed: true,
                  weeklySummary: true
                }
              }
            };
          } else {
            // 兼容老数据：补齐 weeklySummary 字段
            preferences = {
              ...preferences,
              feedback: {
                ...preferences.feedback,
                scenarios: {
                  ...preferences.feedback.scenarios,
                  weeklySummary:
                    preferences.feedback.scenarios.weeklySummary ?? true
                }
              }
            };
          }
        }
        
        setNotificationPreferences(preferences);
      }
    } catch (error) {
      console.error('获取通知偏好设置失败:', error);
    } finally {
      setLoadingPreferences(false);
    }
  }, [userInfo.email]);

  useEffect(() => {
    if (token) {
      fetchNotificationPreferences();
    }
  }, [token, fetchNotificationPreferences]);

  // 更新通知偏好设置
  const handleSaveNotificationPreferences = async (): Promise<boolean> => {
    if (!notificationPreferences) return false;

    try {
      setLoadingPreferences(true);
      const response = await updateNotificationPreferences(notificationPreferences);
      if (response.success) {
        setAlertMsg('通知偏好设置已保存');
        setTimeout(() => setAlertMsg(''), 3000);
        return true;
      }
      setAlertMsg('保存失败: ' + (response.error || '未知错误'));
      setTimeout(() => setAlertMsg(''), 3000);
      return false;
    } catch (error) {
      console.error('保存通知偏好设置失败:', error);
      setAlertMsg('保存失败');
      setTimeout(() => setAlertMsg(''), 3000);
      return false;
    } finally {
      setLoadingPreferences(false);
    }
  };

  // 更新通知偏好设置中的某个值
  const updatePreference = (category: keyof NotificationPreferences, field: string, value: boolean, subField?: string) => {
    if (!notificationPreferences) return;
    
    // 如果尝试启用邮件通知但没有邮箱，阻止操作
    if (field === 'emailEnabled' && value && !userInfo.email) {
      setAlertMsg('请先绑定邮箱才能启用邮件通知');
      setTimeout(() => setAlertMsg(''), 3000);
      return;
    }
    
    const updated = { ...notificationPreferences };
    if (subField) {
      // 更新场景开关
      (updated[category] as any).scenarios[subField] = value;
    } else {
      // 更新主开关
      (updated[category] as any)[field] = value;
    }
    setNotificationPreferences(updated);
  };

  const handlePasswordChange = async () => {
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setAlertMsg('请填写所有密码字段');
      setTimeout(() => setAlertMsg(''), 2000);
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setAlertMsg('新密码与确认密码不匹配');
      setTimeout(() => setAlertMsg(''), 2000);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setAlertMsg('新密码长度至少6位');
      setTimeout(() => setAlertMsg(''), 2000);
      return;
    }

    try {
      setLoading(true);
      const response = await changePassword(passwordForm.oldPassword, passwordForm.newPassword);
      if (response.success) {
        setAlertMsg('密码修改成功');
        setShowPasswordModal(false);
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setAlertMsg('密码修改失败: ' + response.error);
      }
    } catch (error) {
      setAlertMsg('密码修改失败');
    } finally {
      setLoading(false);
      setTimeout(() => setAlertMsg(''), 3000);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // 创建预览URL
      const fileUrl = URL.createObjectURL(file);
      setUserInfo(prev => ({ ...prev, avatar: fileUrl }));
      
      // 立即上传头像
      try {
        setLoading(true);
        const response = await updateProfile(userInfo, file);
        if (response.success) {
          setAlertMsg('头像更新成功');
          // 更新用户上下文中的头像
          updateUserInfo(undefined, response.data?.avatar);
          // 刷新用户信息
          const userResponse = await getCurrentUser();
          if (userResponse.success && userResponse.data) {
            setUserInfo(userResponse.data);
          }
        } else {
          setAlertMsg('头像更新失败: ' + response.error);
        }
      } catch (error) {
        setAlertMsg('头像更新失败');
      } finally {
        setLoading(false);
        setTimeout(() => setAlertMsg(''), 3000);
      }
    }
  };

  const handleEditProfile = async () => {
    try {
      setLoading(true);
      const response = await updateProfile(userInfo, selectedFile || undefined);
      if (response.success) {
        setAlertMsg('个人信息更新成功');
        setShowEditModal(false);
        setSelectedFile(null);
        // 更新用户上下文
        updateUserInfo(undefined, response.data?.avatar);
        // 刷新用户信息
        const userResponse = await getCurrentUser();
        if (userResponse.success && userResponse.data) {
          const hadEmail = userInfo.email;
          setUserInfo(userResponse.data);
          // 如果用户新绑定了邮箱，刷新通知偏好设置（确保邮件通知开关可用）
          if (!hadEmail && userResponse.data.email) {
            fetchNotificationPreferences();
          }
        }
      } else {
        setAlertMsg('更新失败: ' + response.error);
      }
    } catch (error) {
      console.error('更新失败:', error);
      setAlertMsg('更新失败');
    } finally {
      setLoading(false);
      setTimeout(() => setAlertMsg(''), 3000);
    }
  };

  const literaturePct = useMemo(() => {
    if (literatureStorage && literatureStorage.quotaBytes > 0) {
      return Math.min(
        100,
        Math.round((literatureStorage.usedBytes / literatureStorage.quotaBytes) * 100)
      );
    }
    if (!userInfo.literatureQuota) return 0;
    const u = userInfo.literatureUsed ?? 0;
    return Math.min(100, Math.round((u / userInfo.literatureQuota) * 100));
  }, [literatureStorage, userInfo.literatureQuota, userInfo.literatureUsed]);

  const literatureUsedMb =
    (literatureStorage?.usedBytes ?? userInfo.literatureUsed ?? 0) / (1024 * 1024);
  const literatureQuotaMb =
    (literatureStorage?.quotaBytes ?? userInfo.literatureQuota ?? 500 * 1024 * 1024) /
    (1024 * 1024);

  if (!token) {
    return (
      <div className="container mt-4">
        <Alert variant="warning">
          请先登录后再访问个人中心
        </Alert>
      </div>
    );
  }

  return (
    <div className="page-container full-height members-lims profile-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>个人中心</span>
      </nav>

      <div className="content-area px-1 px-md-2 profile-content-max">
        {alertMsg && <Alert variant="success" className="mb-3">{alertMsg}</Alert>}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">加载中...</span>
            </div>
            <p className="mt-2">正在加载用户信息...</p>
          </div>
        ) : (
          <>
            <section className="profile-hero position-relative mb-4">
              <div className="profile-hero-band" aria-hidden />
              <div className="profile-hero-inner d-flex flex-column flex-md-row align-items-end gap-4">
                <div className="profile-hero-avatar-wrap position-relative">
                  <img
                    src={
                      userInfo.avatar?.startsWith('blob:')
                        ? userInfo.avatar
                        : resolveUploadAssetUrl(userInfo.avatar) || userInfo.avatar || defaultAvatar
                    }
                    alt=""
                    className="profile-hero-avatar"
                  />
                  <label htmlFor="avatar-upload-hero" className="profile-hero-avatar-edit" title="更换头像">
                    <LimsNavIcon name="edit" sizePx={14} />
                  </label>
                  <input id="avatar-upload-hero" type="file" accept="image/*" className="d-none" onChange={handleAvatarChange} />
                </div>
                <div className="flex-grow-1 pb-2" style={{ minWidth: 0 }}>
                  <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 w-100">
                    <div>
                      <h2 className="profile-hero-name mb-1">{userInfo.name || '用户'}</h2>
                      <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                        <span className="profile-role-pill">{userInfo.role}</span>
                        {userInfo.supervisor && (
                          <span className="profile-hero-mentor d-flex align-items-center gap-1">
                            <LimsNavIcon name="school" sizePx={18} />
                            导师：{userInfo.supervisor}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <Button className="profile-btn-primary d-inline-flex align-items-center gap-2 rounded-3 px-4 border-0" onClick={() => setShowEditModal(true)}>
                        <LimsNavIcon name="edit_note" sizePx={20} />
                        编辑资料
                      </Button>
                      <Button
                        variant="light"
                        className="profile-btn-secondary d-inline-flex align-items-center gap-2 rounded-3"
                        onClick={() => setShowPasswordModal(true)}
                      >
                        <LimsNavIcon name="lock_reset" sizePx={20} />
                        修改密码
                      </Button>
                      <Button
                        variant="light"
                        className="profile-btn-secondary d-inline-flex align-items-center gap-2 rounded-3"
                        onClick={() => setShowNotificationModal(true)}
                      >
                        <LimsNavIcon name="notifications_active" sizePx={20} />
                        通知偏好设置
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <Row className="g-4 mb-4 align-items-stretch profile-info-and-sidebar-row">
              <Col lg={8} className="d-flex">
                <div className="profile-surface-card profile-personal-card p-4 p-md-5 rounded-4 w-100 h-100 d-flex flex-column">
                  <h3 className="profile-card-title d-flex align-items-center gap-2 mb-3 mb-md-4">
                    <LimsNavIcon name="person" sizePx={22} />
                    个人信息
                  </h3>
                  <div className="profile-info-grid flex-grow-1">
                    <div className="profile-field">
                      <p className="profile-field-label">性别</p>
                      <p className="profile-field-value">{userInfo.gender || '—'}</p>
                    </div>
                    <div className="profile-field">
                      <p className="profile-field-label">学号 / 工号</p>
                      <p className="profile-field-value">{userInfo.studentId || '—'}</p>
                    </div>
                    <div className="profile-field">
                      <p className="profile-field-label">手机</p>
                      <p className="profile-field-value">{userInfo.phone || '—'}</p>
                    </div>
                    <div className="profile-field">
                      <p className="profile-field-label">邮箱</p>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <p className="profile-field-value mb-0">{userInfo.email || '未绑定'}</p>
                        {userInfo.email ? <LimsNavIcon name="verified" sizePx={18} className="text-success" /> : null}
                      </div>
                    </div>
                    <div className="profile-field">
                      <p className="profile-field-label">入学年份</p>
                      <p className="profile-field-value">{userInfo.enrollmentYear ?? '—'}</p>
                    </div>
                    <div className="profile-field">
                      <p className="profile-field-label">成员类型</p>
                      <p className="profile-field-value">{userInfo.studentType || '—'}</p>
                    </div>
                    <div className="profile-field">
                      <p className="profile-field-label">注册时间</p>
                      <p className="profile-field-value">
                        {userInfo.createdAt ? new Date(userInfo.createdAt).toLocaleDateString('zh-CN') : '—'}
                      </p>
                    </div>
                    <div className="profile-field profile-field--full">
                      <p className="profile-field-label">微信小程序</p>
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        {userInfo.wechatAuth?.openid ? (
                          <span className="profile-wechat-badge profile-wechat-badge--on">已绑定</span>
                        ) : (
                          <span className="profile-wechat-badge profile-wechat-badge--off">未绑定</span>
                        )}
                      </div>
                      {userInfo.wechatAuth?.openid ? (
                        <>
                          <p className="profile-wechat-id mb-1">
                            标识：{maskWechatOpenid(userInfo.wechatAuth.openid)}
                          </p>
                          <p className="profile-meta-xs mb-0">
                            {userInfo.wechatAuth.boundAt
                              ? `绑定于 ${formatProfileDateTime(userInfo.wechatAuth.boundAt)}`
                              : userInfo.wechatAuth.lastLoginAt
                                ? `最近微信登录 ${formatProfileDateTime(userInfo.wechatAuth.lastLoginAt)}`
                                : '—'}
                          </p>
                        </>
                      ) : (
                        <p className="profile-meta-xs mb-0">
                          使用实验室小程序登录并完成账号关联后，将显示在此。
                        </p>
                      )}
                    </div>
                    <div className="profile-field profile-field--full">
                      <p className="profile-field-label">网站微信（扫码）</p>
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        {userInfo.wechatWebAuth?.openid ? (
                          <span className="profile-wechat-badge profile-wechat-badge--on">已绑定</span>
                        ) : (
                          <span className="profile-wechat-badge profile-wechat-badge--off">未绑定</span>
                        )}
                      </div>
                      {userInfo.wechatWebAuth?.openid ? (
                        <>
                          <p className="profile-wechat-id mb-1">
                            标识：{maskWechatOpenid(userInfo.wechatWebAuth.openid)}
                          </p>
                          <p className="profile-meta-xs mb-0">
                            {userInfo.wechatWebAuth.boundAt
                              ? `绑定于 ${formatProfileDateTime(userInfo.wechatWebAuth.boundAt)}`
                              : userInfo.wechatWebAuth.lastLoginAt
                                ? `最近扫码登录 ${formatProfileDateTime(userInfo.wechatWebAuth.lastLoginAt)}`
                                : '—'}
                          </p>
                        </>
                      ) : (
                        <p className="profile-meta-xs mb-0">
                          在登录页使用「微信扫码登录」并成功进入系统后，将与此账号关联。
                        </p>
                      )}
                    </div>
                    <div className="profile-field profile-field--full">
                      <p className="profile-field-label">备用联系</p>
                      {!userInfo.email ? (
                        <div className="profile-warn-chip d-inline-flex align-items-center gap-2">
                          <LimsNavIcon name="warning" sizePx={16} />
                          <span>未绑定邮箱</span>
                        </div>
                      ) : (
                        <p className="profile-field-value mb-0">已绑定邮箱</p>
                      )}
                    </div>
                  </div>
                </div>
              </Col>

              <Col lg={4} className="d-flex flex-column gap-4 profile-sidebar-stack">
                <div className="profile-surface-card profile-lit-card p-4 rounded-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="profile-side-title mb-0">
                      {literatureStorage?.scope === 'team' ? '团队文献存储' : '文献存储'}
                    </h3>
                    <span className="profile-lit-badge">{literaturePct}%</span>
                  </div>
                  <div className="profile-progress rounded-pill">
                    <div className="profile-progress-bar" style={{ width: `${literaturePct}%` }} />
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <span className="small fw-semibold profile-meta-strong">
                      {literatureUsedMb.toFixed(1)} MB / {literatureQuotaMb.toFixed(0)} MB
                    </span>
                    <button type="button" className="btn btn-link btn-sm p-0 text-uppercase fw-bold profile-link-action">
                      申请扩容
                    </button>
                  </div>
                </div>

                <div className="profile-archives-cta p-4 rounded-4 text-white">
                  <LimsNavIcon name="science" sizePx={28} className="d-block mb-2 opacity-75" />
                  <h3 className="profile-archives-title mb-2">我的实验记录</h3>
                  <p className="profile-archives-desc mb-4">进入实验记录工作台，管理实验记录与实验数据。</p>
                  <Button
                    variant="light"
                    className="w-100 py-2 rounded-3 fw-bold d-inline-flex align-items-center justify-content-center gap-2 profile-archives-btn border-0"
                    onClick={() => navigate('/workbench/experiment-records')}
                  >
                    打开实验记录本
                    <LimsNavIcon name="arrow_forward" sizePx={18} />
                  </Button>
                </div>

                <div className="profile-surface-card p-4 rounded-4 profile-security-card">
                  <h3 className="profile-side-title d-flex align-items-center gap-2 mb-4">
                    <LimsNavIcon name="shield" sizePx={22} />
                    安全
                  </h3>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <p className="small fw-bold mb-1">登录密码</p>
                      <p className="profile-meta-xs mb-0">
                        {userInfo.passwordChanged ? '已修改过密码' : '建议定期更换'}
                      </p>
                    </div>
                    <Button size="sm" className="rounded-3 fw-bold text-uppercase profile-outline-btn" onClick={() => setShowPasswordModal(true)}>
                      更新
                    </Button>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="small fw-bold mb-1">双因素认证</p>
                      <p className="profile-meta-xs mb-0">即将支持</p>
                    </div>
                    <Form.Check type="checkbox" disabled aria-label="2FA" className="m-0" />
                  </div>
                </div>
              </Col>
            </Row>

            <footer className="profile-footer-meta d-flex flex-wrap gap-4 py-4 mt-2">
              <span>创建 · {userInfo.createdAt ? new Date(userInfo.createdAt).toLocaleString('zh-CN') : '—'}</span>
              <span>更新 · {userInfo.updatedAt ? new Date(userInfo.updatedAt).toLocaleString('zh-CN') : '—'}</span>
            </footer>
          </>
        )}
      </div>

      {/* 编辑信息模态框 */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>编辑个人信息</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>姓名 *</Form.Label>
                  <Form.Control
                    type="text"
                    value={userInfo.name || ''}
                    onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>性别</Form.Label>
                  <Form.Select
                    value={userInfo.gender || '男'}
                    onChange={(e) => setUserInfo({ ...userInfo, gender: e.target.value as '男' | '女' | '其他' })}
                  >
                    <option value="男">男</option>
                    <option value="女">女</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>学号/工号 *</Form.Label>
                  <Form.Control
                    type="text"
                    value={userInfo.studentId || ''}
                    onChange={(e) => setUserInfo({ ...userInfo, studentId: e.target.value })}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>联系电话</Form.Label>
                  <Form.Control
                    type="tel"
                    value={userInfo.phone || ''}
                    onChange={(e) => setUserInfo({ ...userInfo, phone: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>邮箱</Form.Label>
                  <Form.Control
                    type="email"
                    value={userInfo.email || ''}
                    onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>导师</Form.Label>
                  <Form.Select
                    value={userInfo.supervisor || ''}
                    onChange={(e) => setUserInfo({ ...userInfo, supervisor: e.target.value })}
                    disabled={userInfo.studentType === '教师'}
                  >
                    <option value="">请选择导师</option>
                    {teachers.map(teacher => (
                      <option key={teacher._id} value={teacher.name}>
                        {teacher.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>入学年份</Form.Label>
                  <Form.Control
                    type="number"
                    value={userInfo.enrollmentYear || new Date().getFullYear()}
                    onChange={(e) => setUserInfo({ ...userInfo, enrollmentYear: parseInt(e.target.value) })}
                    min="2000"
                    max={new Date().getFullYear() + 10}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>成员类型</Form.Label>
                  <Form.Select
                    value={userInfo.studentType || '研究生'}
                    onChange={(e) => setUserInfo({ ...userInfo, studentType: e.target.value as '研究生' | '博士' | '博士后' | '教师' })}
                  >
                    <option value="本科生">本科生</option>
                    <option value="研究生">研究生</option>
                    <option value="博士">博士</option>
                    <option value="教师">教师</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>头像</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const target = e.target as HTMLInputElement;
                      const file = target.files?.[0];
                      if (file) {
                        setSelectedFile(file);
                        const fileUrl = URL.createObjectURL(file);
                        setUserInfo({ ...userInfo, avatar: fileUrl });
                      }
                    }}
                  />
                </Form.Group>
                <div className="mb-3">
                  <Form.Label>头像预览</Form.Label>
                  <div className="border rounded p-2" style={{ width: '100px', height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
                    <img
                      src={
                        !userInfo.avatar
                          ? '/default-avatar.svg'
                          : userInfo.avatar.startsWith('blob:')
                            ? userInfo.avatar
                            : resolveUploadAssetUrl(userInfo.avatar) ||
                              userInfo.avatar ||
                              getDefaultAvatarByGender(userInfo.gender)
                      }
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                      onError={(e) => {
                        e.currentTarget.src = '/default-avatar.svg';
                      }}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleEditProfile} disabled={loading}>
            {loading ? '保存中...' : '保存'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 密码修改模态框 */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>修改密码</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>当前密码</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.oldPassword}
                onChange={e => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>新密码</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.newPassword}
                onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>确认新密码</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.confirmPassword}
                onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPasswordModal(false)} disabled={loading}>
            取消
          </Button>
          <Button variant="primary" onClick={handlePasswordChange} disabled={loading}>
            {loading ? '修改中...' : '确定'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showNotificationModal}
        onHide={() => setShowNotificationModal(false)}
        size="lg"
        scrollable
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>通知偏好设置</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NotificationPreferencesPanel
            userInfo={userInfo}
            notificationPreferences={notificationPreferences}
            loadingPreferences={loadingPreferences}
            updatePreference={updatePreference}
            onRequestBindEmail={() => {
              setShowNotificationModal(false);
              setShowEditModal(true);
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNotificationModal(false)}>
            取消
          </Button>
          <Button
            className="profile-save-btn border-0"
            disabled={loadingPreferences || !notificationPreferences}
            onClick={async () => {
              const ok = await handleSaveNotificationPreferences();
              if (ok) setShowNotificationModal(false);
            }}
          >
            {loadingPreferences ? '保存中…' : '保存设置'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserProfile; 